/* ==========================================================================
   AB homepage behaviour (Framer parity, 2026-09-25). No dependencies.
   Everything scroll-linked runs in ONE rAF-throttled scroll handler and only
   writes transform/opacity/class — never layout. Sections register themselves
   by data attribute; absent JS the page is static but complete.
     data-abh-stack   beats fade in/out around the viewport centre
     data-abh-mx      organ stage + active card + connector
     data-abh-close   photo columns slide apart by progress (--abh-p); photos step by progress
     data-abh-tech    one open card at a time (hover / focus / tap)
     data-abh-one     blur-in reveal (IntersectionObserver)
   ========================================================================== */
(function () {
  'use strict';
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var vh = window.innerHeight;
  var jobs = [];

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* ---- hero: video is SCRUBBED by scroll (Framer: paused, currentTime 0->10s over
     the first ~1,300px), not autoplayed. ab-motion.js attaches the sources and starts
     playback when the hero is on screen; we pause on the first 'playing' and drive
     currentTime from scroll. Seeks are skipped under 1/25s so the decoder is not
     hammered; reduced-motion visitors keep the poster (ab-motion never loads video). */
  function initHeroScrub() {
    var hero = document.querySelector('.abh-hero');
    if (!hero || reduced) return;
    var video = hero.querySelector('.ab-bgv__video');
    if (!video) return;
    /* af72: theme setting "Phone hero motion" — autoplay-and-hold on phones plays the clip once (ab-motion starts it) and
       leaves it on its last frame; the zoom below still tracks scroll. Desktop always scrubs. */
    var phoneAuto = hero.getAttribute('data-abh-hero-mobile') === 'autoplay' && window.matchMedia && window.matchMedia('(max-width: 899.98px)').matches;
    var range = 1300;
    var ready = false, last = -1;
    video.dataset.abScrub = '1';
    function seek() {
      var y = window.pageYOffset;
      /* Framer: the clip sits at 50% opacity and grows ~1.0 -> ~1.96 over the first 2,000px, so the
         figures leave the frame; measured 900px tall at y=0, 1160 at 600, 1464 at 1300, 1767 at 2000. */
      hero.style.setProperty('--abh-hero-zoom', (1 + 0.00048 * clamp(y, 0, 2700)).toFixed(3) /* af75: Framer keeps zooming through the release (video h 900->2070 by scroll 2700) */);
      /* Framer: the opening copy leaves ~1.8x faster than the page (parallax exit) and is gone by ~700px */
      /* af30: Framer hero copy scrolls 1:1 at opacity 1 (measured h1 top 274 -> -1026 over 1300px, opacity 1 throughout); no parallax, no fade */
      hero.style.setProperty('--abh-hx', '0px');
      hero.style.setProperty('--abh-ho', '1');
      if (!ready) return;
      var dur = video.duration;
      if (!dur || dur === Infinity) return;
      var p = clamp(y / range, 0, 1);
      var t = p * (dur - 0.05);
      if (Math.abs(t - last) < 0.04) return;
      last = t;
      try { video.currentTime = t; } catch (e) { /* not seekable yet */ }
    }
    if (phoneAuto) {
      video.removeAttribute('loop');
      video.addEventListener('playing', function () { video.classList.add('is-playing'); }, { once: true });
      jobs.push(function () { hero.style.setProperty('--abh-hero-zoom', (1 + 0.00048 * clamp(window.pageYOffset, 0, 2700)).toFixed(3)); hero.style.setProperty('--abh-hx', '0px'); hero.style.setProperty('--abh-ho', '1'); });
      return;
    }
    video.addEventListener('playing', function onPlay() {
      video.pause();
      video.classList.add('is-playing');
      video.removeAttribute('loop');
      ready = true;
      seek();
    });
    /* ab-motion re-calls play() when the hero re-enters the viewport; keep it paused. */
    video.addEventListener('play', function () { if (ready) video.pause(); });
    jobs.push(seek);
  }

  /* ---- statement stack: opacity by distance from viewport centre ---- */
  function initStacks() {
    var stacks = document.querySelectorAll('[data-abh-stack]');
    if (!stacks.length || reduced) return;
    var beats = [];
    var narrow = window.matchMedia && window.matchMedia('(max-width: 899.98px)').matches;
    for (var i = 0; i < stacks.length; i++) {
      var b = stacks[i].querySelectorAll('.abh-stack__beat');
      for (var j = 0; j < b.length; j++) beats.push(b[j]);
    }
    /* af58: Framer 390 renders "The next molecule. / procedure. / biohack." as three stacked lines (separate <p>,
       pitch 46px) under a 24px gap; desktop keeps them inline. Wrap the richtext's nbsp gaps + <br> in spans so CSS
       can switch them per viewport without touching the richtext. */
    var bodies = document.querySelectorAll('[data-abh-stack] .abh-stack__b p');
    for (var bi = 0; bi < bodies.length; bi++) {
      var html = bodies[bi].innerHTML;
      if (html.indexOf('&nbsp;&nbsp;&nbsp;') === -1) continue;
      bodies[bi].innerHTML = html.replace(/<br\s*\/?>/g, '<span class="abh-gap abh-gap--nl"></span>').replace(/(&nbsp;){3}/g, '<span class="abh-gap">&nbsp;&nbsp;&nbsp;</span>');
    }
    /* af58: Framer's 390 variant hand-breaks the "knows" beat (SSR newlines inside pre-wrap): THE BODY / ALREADY KNOWS /
       HOW TO ORGANIZE, / ADAPT, REGENERATE / AND ELECTRIFY. Desktop keeps the richtext's own breaks. */
    if (narrow) {
      for (var ni = 0; ni < bodies.length; ni++) {
        var nh = bodies[ni].innerHTML;
        if (nh.indexOf('already knows') === -1) continue;
        bodies[ni].innerHTML = nh.replace(/The body\s+<strong>/, 'The body<br><strong>').replace(/organize,\s*adapt,\s*<br>\s*regenerate,\s*and electrify/, 'organize,<br>adapt, regenerate,<br>and electrify');
      }
      /* Framer 390 variant: THE PRIMORDIAL / SYSTEM / OF FASCIA */
      var lastH = document.querySelectorAll('[data-abh-stack] .abh-stack__h--last');
      for (var li = 0; li < lastH.length; li++) lastH[li].innerHTML = lastH[li].innerHTML.replace(/System\s+of Fascia/i, function (m) { return m.split(/\s+/)[0] + '<br>' + m.split(/\s+/).slice(1).join(' '); });
    }
    if ('IntersectionObserver' in window) {
      var popIO = new IntersectionObserver(function (ents) {
        for (var e = 0; e < ents.length; e++) {
          var inner = ents[e].target;
          /* Framer trigger: element centre inside the viewport (threshold .5 both ways) */
          inner.classList.toggle('is-on', ents[e].intersectionRatio >= 0.5 || (ents[e].isIntersecting && ents[e].boundingClientRect.height > vh));
        }
      }, { threshold: [0, 0.5, 1] });
      for (var q = 0; q < beats.length; q++) {
        var innerEl = beats[q].querySelector('.abh-stack__in');
        if (innerEl) popIO.observe(innerEl);
      }
    } else {
      for (var q2 = 0; q2 < beats.length; q2++) { var ie = beats[q2].querySelector('.abh-stack__in'); if (ie) ie.classList.add('is-on'); }
    }
    jobs.push(function () {
      var mid = vh / 2;
      for (var k = 0; k < beats.length; k++) {
        var r = beats[k].getBoundingClientRect();
        if (r.bottom < -vh || r.top > vh * 2) continue;
        var c = r.top + r.height / 2;
        var d = Math.abs(c - mid) / (vh * 0.5);
        /* Framer: the last beat ("The Primordial System of Fascia") stays full white until it scrolls off; no fade-out */
        if (c < mid && !beats[k].nextElementSibling.classList.contains('abh-stack__beat')) d = 0;
        /* Framer: the first beat is fully lit while it rises from below the fold; it only fades after passing centre */
        if (c > mid && !(beats[k].previousElementSibling && beats[k].previousElementSibling.classList.contains('abh-stack__beat'))) d = 0;
        /* phones: Framer beats read full white the whole way through; only the edges ease */
        /* Framer: outgoing copy is still faintly visible while the next beat enters; no blank-cell frames between beats */
        var o = narrow ? clamp((1 - d) * 5, 0, 1) : clamp(1.3 - d, 0, 1);
        /* af56: harvest (Story 1/3/5, both viewports) shows the copy is plain in-flow content with NO drift and NO
           scroll-scrubbed opacity: transform is `none` while in view; it pops in (scale .5->1, opacity 0->1, ~.3s
           spring) when its centre crosses the bottom edge and pops out the same way when its centre crosses the top.
           The scrub var stays at 1 for the harness; the pop is driven by the IntersectionObserver below. */
        beats[k].style.setProperty('--abh-scrub', '1');
        beats[k].style.setProperty('--abh-drift', '0px');
      }
      /* Framer: the cell video starts small and grows to full size over the first half of the section. */
      for (var s = 0; s < stacks.length; s++) {
        var sr = stacks[s].getBoundingClientRect();
        if (sr.bottom < 0 || sr.top > vh) continue;
        var prog = clamp(-sr.top / (sr.height - vh), 0, 1);
        var z = 1, op = 0.55 - 0.40 * prog;
        if (narrow) {
          /* af80: the Framer does NOT keep the cell full-bleed on phones (the old comment here was wrong). Sampled live at
             390x844 every 150px (2026-09-27): section top -> rendered video WIDTH; it grows from 0 as the section rises
             into view (45 @788, 293 @488, 541 @38), continues past the pin (817 @-262, 1287 @-712, 1361 @-1012), eases
             to ~2,270 by -1,912 and then breathes around ~2,500-2,800 (envelope held). Opacity .70 -> .54 @-112 -> .15 @-2,512.
             Keyed on section top normalised to an 844 viewport. */
          /* af81: re-sampled as the Framer's real transform (scale of its fixed 1440x1124 box, not the rotated AABB width af80 stored):
             scale grows linearly (844 - top)/2185 from 0 at section entry to 1.51 at -2,456, peaks 1.57 at the release and eases to
             ~1.51; opacity is linear .70 -> .15 over the same run. --abh-zoom is now the box scale (CSS puts it on the box). */
          var ZPTS_M = [[844, 0.0005], [-2456, 1.514], [-2606, 1.568], [-2906, 1.544], [-3206, 1.505]];
          var OPTS_M = [[844, 0.70], [-2606, 0.15]];
          var tm = sr.top * 844 / vh;
          function lutm(pts, x) {
            if (x >= pts[0][0]) return pts[0][1];
            for (var q = 1; q < pts.length; q++) {
              if (x >= pts[q][0]) { var f = (pts[q - 1][0] - x) / (pts[q - 1][0] - pts[q][0]); return pts[q - 1][1] + f * (pts[q][1] - pts[q - 1][1]); }
            }
            return pts[pts.length - 1][1];
          }
          z = lutm(ZPTS_M, tm);
          op = lutm(OPTS_M, tm);
        } else {
          /* af76: Framer cell video, sampled live every 200px at 1440x900 (2026-09-27): section top -> rendered WIDTH of the
             near-square (1.075) video box, and opacity. It scales in while the section is still below the fold (so the
             nucleus shows under the hero CTA), holds ~589 for the first 200px of the pin, then eases in to ~2,730 by the
             release; opacity .70 -> .58 at the pin -> .15 by -2,900. Keyed on section top normalised to a 900 viewport. */
          var WPTS = [[900, 0], [699, 129], [499, 286], [299, 446], [99, 589], [-101, 589], [-301, 816], [-501, 877], [-701, 949], [-901, 1104], [-1101, 1285], [-1301, 1493], [-1501, 1692], [-1701, 1894], [-1901, 2122], [-2101, 2345], [-2301, 2522], [-2501, 2629], [-2701, 2730]];
          var OPTS = [[900, 0.70], [99, 0.59], [-101, 0.58], [-301, 0.52], [-2901, 0.15]];
          var tn = sr.top * 900 / vh;
          function lut(pts, x) {
            if (x >= pts[0][0]) return pts[0][1];
            for (var q = 1; q < pts.length; q++) {
              if (x >= pts[q][0]) { var f = (pts[q - 1][0] - x) / (pts[q - 1][0] - pts[q][0]); return pts[q - 1][1] + f * (pts[q][1] - pts[q - 1][1]); }
            }
            return pts[pts.length - 1][1];
          }
          z = Math.max(0.01, lut(WPTS, tn) / 1440);
          op = lut(OPTS, tn);
        }
        stacks[s].style.setProperty('--abh-zoom', z.toFixed(3));
        stacks[s].style.setProperty('--abh-media-op', op.toFixed(3));
      }
    });
  }

  /* ---- matrix: card nearest the stage centre is active; organ follows ---- */
  function initMatrix() {
    var roots = document.querySelectorAll('[data-abh-mx]');
    for (var i = 0; i < roots.length; i++) (function (root) {
      var cards = root.querySelectorAll('.abh-mx__card');
      var imgs = root.querySelectorAll('.abh-mx__img');
      if (!cards.length) return;
      var current = -1;
      function activate(n) {
        if (n === current) return;
        current = n;
        for (var c = 0; c < cards.length; c++) cards[c].classList.toggle('is-active', c === n);
        for (var m = 0; m < imgs.length; m++) imgs[m].classList.toggle('is-active', m === n);
      }
      activate(0);
      /* Framer: the connector is a live wireframe cluster (slow 3D tumble, twinkling nodes) with filament
         threads flowing from the organ into the cluster and out to the lit card. Canvas, rAF only while the
         section is on screen; one static frame under prefers-reduced-motion. Zero network cost. */
      var web = root.querySelector('canvas.abh-mx__web');
      var webState = null;
      if (web && web.getContext) (function () {
        var ctx = web.getContext('2d');
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var N = 30, pts = [], edges = [], k; /* af36: Framer SVG = 24 nodes, curved dendrites from the centre, no pulses */
        function rnd(seed) { var x = Math.sin(seed * 9301 + 49297) * 233280; return x - Math.floor(x); }
        for (k = 0; k < N; k++) {
          var u = rnd(k + 1) * 2 - 1, th = rnd(k + 101) * Math.PI * 2, rr = 0.55 + rnd(k + 201) * 0.5;
          var s = Math.sqrt(1 - u * u);
          pts.push({ x: s * Math.cos(th) * rr, y: u * rr, z: s * Math.sin(th) * rr, tw: rnd(k + 301) * 6.28 });
        }
        for (k = 0; k < N; k++) for (var j = k + 1; j < N; j++) {
          var dx = pts[k].x - pts[j].x, dy = pts[k].y - pts[j].y, dz = pts[k].z - pts[j].z;
          if (dx * dx + dy * dy + dz * dz < 0.42) edges.push([k, j]); /* af37: denser cage like the Framer's polyhedral cluster */
        }
        var threads = [];
        for (k = 0; k < 14; k++) threads.push({ side: k < 8 ? -1 : 1, off: rnd(k + 401) * 2 - 1, amp: 0.25 + rnd(k + 501) * 0.5, ph: rnd(k + 601) * 6.28, sp: 0.35 + rnd(k + 701) * 0.5 });
        var W = 0, H = 0, dpr = 1, targetY = 0.5, tgt = 0.5, running = false, t0 = 0;
        function size() {
          var r = web.getBoundingClientRect();
          if (!r.width || !r.height) return false;
          dpr = Math.min(window.devicePixelRatio || 1, 2);
          if (W !== r.width || H !== r.height) { W = r.width; H = r.height; web.width = Math.round(W * dpr); web.height = Math.round(H * dpr); }
          return true;
        }
        function draw(ts) {
          if (!size()) return;
          var t = (ts - t0) / 1000, mobile = !(window.matchMedia && window.matchMedia('(min-width: 900px)').matches); /* af29: viewport decides, not canvas box (a 500px link box at 1440 was read as mobile) */
          tgt += (targetY - tgt) * 0.06;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.clearRect(0, 0, W, H);
          var cx = W * (mobile ? 0.5 : 0.5), cy = H * (mobile ? 0.5 : 0.5), R = Math.min(W * 0.42, H * 0.28, 128); /* Framer: 256px cluster */
          if (mobile) R = Math.min(W * 0.28, H * 0.42); /* Framer 390: cluster ~55% of viewport width */
          var ay = t * 0.22, ax = 0.35 + Math.sin(t * 0.17) * 0.25, cA = Math.cos(ay), sA = Math.sin(ay), cB = Math.cos(ax), sB = Math.sin(ax);
          var proj = [];
          for (k = 0; k < N; k++) {
            var p = pts[k], x1 = p.x * cA - p.z * sA, z1 = p.x * sA + p.z * cA, y1 = p.y * cB - z1 * sB, z2 = p.y * sB + z1 * cB;
            var sc = 1 / (1.45 - z2 * 0.4);
            proj.push({ x: cx + x1 * R * sc, y: cy + y1 * R * sc, d: (z2 + 1) / 2, sc: sc });
          }
          /* filaments: organ (left edge) -> cluster, cluster -> lit card (right edge, at the card's height) */
          for (k = 0; k < threads.length; k++) {
            var th = threads[k], w = Math.sin(t * th.sp + th.ph), sx, sy, ex, ey, c1x, c1y, c2x, c2y;
            if (mobile) {
              /* af28: Framer at 390 stacks organ / cluster / cards, so the filaments run vertically: organ (top edge) -> cluster -> lit card (bottom edge) */
              sy = th.side < 0 ? 0 : H; sx = cx + th.off * W * 0.22;
              ey = cy + th.side * R * 0.55 * (0.6 + 0.4 * Math.abs(th.off)); ex = cx + th.off * R * 0.7;
              c1y = sy + (ey - sy) * 0.35; c1x = sx + w * th.amp * 26; c2y = sy + (ey - sy) * 0.7; c2x = ex - w * th.amp * 18;
            } else {
              sx = th.side < 0 ? 0 : W; sy = th.side < 0 ? cy + th.off * H * 0.14 : tgt * H + th.off * 26;
              ex = cx + th.side * R * 0.55 * (0.6 + 0.4 * Math.abs(th.off)); ey = cy + th.off * R * 0.7;
              c1x = sx + (ex - sx) * 0.35; c1y = sy + w * th.amp * 60; c2x = sx + (ex - sx) * 0.7; c2y = ey - w * th.amp * 40;
            }
            ctx.beginPath(); ctx.moveTo(sx, sy); ctx.bezierCurveTo(c1x, c1y, c2x, c2y, ex, ey);
            ctx.strokeStyle = 'rgba(255,232,200,' + (th.side < 0 ? 0.36 : 0.5) + ')'; ctx.lineWidth = 1.1; ctx.stroke();
            /* af36: no travelling pulses — the Framer's filaments are plain curves */
          }
          /* af36: Framer cluster = curved dendrites from the nucleus to each node (fully connected tree) */
          for (k = 0; k < N; k++) {
            var nd = proj[k], bx = cx + (nd.x - cx) * 0.45 + Math.sin(t * 0.6 + k) * 6, by = cy + (nd.y - cy) * 0.45 + Math.cos(t * 0.5 + k) * 6;
            ctx.beginPath(); ctx.moveTo(cx, cy); ctx.quadraticCurveTo(bx, by, nd.x, nd.y);
            ctx.lineWidth = 0.6 + nd.d * 0.4; ctx.strokeStyle = 'rgba(247,223,198,' + (0.28 + nd.d * 0.3).toFixed(3) + ')'; ctx.stroke();
          }
          ctx.lineWidth = 0.6;
          for (k = 0; k < edges.length; k++) {
            var a = proj[edges[k][0]], b = proj[edges[k][1]];
            ctx.strokeStyle = 'rgba(247,223,198,' + (0.16 + ((a.d + b.d) / 2) * 0.3).toFixed(3) + ')';
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
          for (k = 0; k < N; k++) {
            var n = proj[k];
            ctx.beginPath(); ctx.arc(n.x, n.y, (1.1 + n.d * 0.55) * n.sc, 0, 6.28);
            ctx.fillStyle = 'rgba(247,223,198,' + (0.6 + n.d * 0.35).toFixed(3) + ')'; ctx.fill();
          }
          var halo = [[13.5, 0.025], [8.46, 0.055], [4.86, 0.12], [5, 0.9]];
          for (k = 0; k < halo.length; k++) { ctx.beginPath(); ctx.arc(cx, cy, halo[k][0], 0, 6.28); ctx.fillStyle = 'rgba(247,223,198,' + halo[k][1] + ')'; ctx.fill(); }
          ctx.beginPath(); ctx.arc(cx, cy, 2, 0, 6.28); ctx.fillStyle = 'rgba(255,255,255,.95)'; ctx.fill();
        }
        function loop(ts) { if (!running) return; draw(ts); requestAnimationFrame(loop); }
        webState = {
          start: function () { if (running || reduce) { if (reduce && !running) { running = true; t0 = 0; draw(2600); running = false; } return; } running = true; if (!t0) t0 = performance.now(); requestAnimationFrame(loop); },
          stop: function () { running = false; },
          target: function (y) { targetY = y; }
        };
      })();
      var track = root.querySelector('.abh-mx__track');
      var col = root.querySelector('.abh-mx__col');
      var desktop = window.matchMedia && window.matchMedia('(min-width: 900px)').matches;
      /* af92: on phones the card lighting runs from a continuous frame loop while the section is on screen, not from the
         scroll event (iOS delivers scroll events unevenly during momentum; the class toggles then landed mid-frame). The
         loop only toggles a class when the nearest card changes, so per-frame cost is 6 rect reads. */
      var mxLoopOn = false;
      function mxFrame() { if (!mxLoopOn) return; mxJob(); window.requestAnimationFrame(mxFrame); }
      function mxJob() {
        var rr = root.getBoundingClientRect();
        if (rr.bottom < 0 || rr.top > vh) { if (webState) webState.stop(); mxLoopOn = false; return; }
        if (webState) webState.start();
        if (!desktop && !mxLoopOn && !reduced) { mxLoopOn = true; window.requestAnimationFrame(mxFrame); }
        if (desktop && track && col) {
          /* Framer: the card column travels the full pinned range (6,066px) while the stage holds. */
          var tr = track.getBoundingClientRect();
          var total = tr.height - vh;
          var p = total > 0 ? clamp(-tr.top / total, 0, 1) : 0;
          var last = cards[cards.length - 1];
          var lastMid = last.offsetTop + last.offsetHeight / 2;
          col.style.setProperty('--abh-col', (-p * lastMid).toFixed(1) + 'px');
        }
        var mid = vh / 2, best = 0, bestD = Infinity;
        for (var c = 0; c < cards.length; c++) {
          var r = cards[c].getBoundingClientRect();
          var d = Math.abs(r.top + r.height / 2 - mid);
          if (d < bestD) { bestD = d; best = c; }
        }
        activate(best);
        if (webState && web) {
          /* thread the lit card: its centre, relative to the canvas box */
          var cr = cards[best].getBoundingClientRect(), wr = web.getBoundingClientRect();
          if (wr.height) webState.target(clamp((cr.top + cr.height / 2 - wr.top) / wr.height, 0.05, 0.95));
        }
      }
      jobs.push(function () { if (desktop || !mxLoopOn) mxJob(); });
    })(roots[i]);
  }

  /* ---- closing (Framer Pre-Scroll): columns slide apart with scroll (--abh-p). Photos are two Framer
     canvas components (Ws left / ic right), each a stack of 3 images (z 3/2/1) cycling variants on timers
     that start on mount: V1 holds 3200ms (left) / 3000ms (right), then V2 (image 1 -> 0), 3000ms, V3
     (image 2 -> 0), 3000ms, back to V1 (both -> 1) and repeat. Every change is spring 2s, delay .2s
     (CSS). Cycle 9.2s left / 9.0s right, so the columns drift out of phase like the Framer's. ---- */
  function initClosing() {
    var roots = document.querySelectorAll('[data-abh-close]');
    for (var i = 0; i < roots.length; i++) (function (root) {
      var cols = root.querySelectorAll('.abh-close__col');
      if (!cols.length) return;
      root.classList.add('is-js');
      var narrow = window.matchMedia && window.matchMedia('(max-width: 899.98px)').matches;
      function cycle(col, firstHold) {
        var state = 1;
        function set(v) { state = v; col.classList.toggle('is-v2', v === 2); col.classList.toggle('is-v3', v === 3); }
        function step() {
          if (reduced) return;
          set(state === 1 ? 2 : state === 2 ? 3 : 1);
          setTimeout(step, state === 1 ? firstHold : 3000);
        }
        set(1);
        setTimeout(step, firstHold);
      }
      cycle(cols[0], 3200);
      if (cols[1]) cycle(cols[1], 3000);
      /* af80: scroll-linked vars are written on the PHOTO layer (.abh-close__pre), not the section root. Every consumer
         (--abh-p, --abh-q, --abh-qx on the columns/slides) lives inside it, and writing six custom properties per frame on
         an ancestor of the sticky text panel made WebKit re-snap the panel every frame (Greg's "vibrating" copy at 390).
         --abh-in/--abh-tx/--abh-fade had no consumers and are gone. */
      var pre = root.querySelector('.abh-close__pre') || root;
      jobs.push(function () {
        var r = root.getBoundingClientRect();
        var total = r.height - vh;
        if (total <= 0) return;
        var p = clamp(-r.top / total, 0, 1);
        /* af129 (Greg 10/3, iPhone 16: lower Ashley photo still shakes after af128). On phones the two photo columns are
           position:sticky children of .abh-close__pre, and --abh-p/--abh-q have NO phone consumers (desktop-only rules), yet both were
           still written on that parent every scroll frame: the exact af80 pattern (ancestor style write -> WebKit re-snaps the sticky
           layer on the main thread each frame, a frame behind the compositor). Phones skip the writes; desktop unchanged. */
        if (!narrow) pre.style.setProperty('--abh-p', p.toFixed(4));
        /* af73b: Framer photo-column entry fires when the section top passes vh/2 (spring in from +-300px); never re-hides */
        if (r.top < vh * 0.5 && !root.classList.contains('is-enter')) root.classList.add('is-enter');
        /* af57: Framer 390 slices translate 1000px over the FULL section height (harvest: 6.57px per 20px scroll = 1000/3044), continuing while the section scrolls away */
        if (!narrow) pre.style.setProperty('--abh-q', clamp(-r.top / r.height, 0, 1).toFixed(4));
        /* af72: whole-px slide offset for the phone columns (WebKit jitters on fractional translates under sticky) */
        /* af81: on phones the section is now content-height (no pinned panel), so the slices run at a fixed 0.6px per scrolled px
           (off-screen by +650, capped at the Framer's 1000) instead of a fraction of a 3,044px section. */
        if (!narrow) pre.style.setProperty('--abh-qx', Math.round(clamp(-r.top / r.height, 0, 1) * 1000) + 'px');
      });
      /* af107 (Milan 9/30 #4, "images shaking left and right" on a real iPhone): the phone slide was written from the shared
         scroll-event -> rAF job, and BrowserStack iPhone 15 frames showed it landing a frame late on ~1 in 7 frames (x moved
         on frames where scrollY had not, then sat still while scrollY moved; up to ~7px out of step) = a sideways judder.
         Phones now drive the two slides from their own per-frame rAF loop while the section is near the viewport: every frame
         reads scrollY and writes the transform directly on the slide (no custom property, no ancestor style write), snapped
         to device pixels. Same curve as before: 0.6px per scrolled px past the section top, capped at 1000. */
      /* af128 (Greg 10/3, real iPhone 16: lower photo still shook as it slid out). af107's per-frame rAF read scrollY and wrote the
         slide's transform from the main thread; on iOS Safari that write lands a frame behind the compositor's momentum scroll, so a
         sideways offset tied to scroll pixels judders against the page however it is snapped. The phone slide is now TRIGGERED, not
         scroll-linked: once the section top passes the viewport top the slides glide out on a CSS transition (run by the compositor,
         nothing to fall behind) and glide back if the reader scrolls back above it. Same direction and end state as the Framer
         (0.6px/px, gone by ~+650); DELIBERATE DEVIATION: timed (1.1s) instead of finger-scrubbed. */
      if (narrow && !reduced) {
        var sl = root.querySelector('.abh-close__col--l .abh-close__slide');
        var sr = root.querySelector('.abh-close__col--r .abh-close__slide');
        var nav0 = navigator, ua0 = nav0.userAgent || '';
        var iosDev = /iPhone|iPad|iPod/i.test(ua0) || (nav0.platform === 'MacIntel' && nav0.maxTouchPoints > 1);
        if (sl && sr && !iosDev) { /* non-iOS phones keep af107's scroll-linked loop (no judder reported there; Framer parity) */
          var dpr = window.devicePixelRatio || 1, lastQ = -1, live = false, rid = 0;
          var tick = function () {
            if (!live) { rid = 0; return; }
            var q = Math.min(1000, Math.max(0, -root.getBoundingClientRect().top) * 0.6);
            q = Math.round(q * dpr) / dpr;
            if (q !== lastQ) { lastQ = q; sl.style.transform = 'translate3d(' + (-q) + 'px,0,0)'; sr.style.transform = 'translate3d(' + q + 'px,0,0)'; }
            rid = window.requestAnimationFrame(tick);
          };
          if ('IntersectionObserver' in window) {
            new IntersectionObserver(function (en) {
              live = en[0].isIntersecting;
              if (live && !rid) rid = window.requestAnimationFrame(tick);
            }, { rootMargin: '100% 0px' }).observe(root);
          } else { live = true; rid = window.requestAnimationFrame(tick); }
        } else if (sl && sr) {
          var out = null, ease = 'transform 1.1s cubic-bezier(.45,0,.2,1)';
          sl.style.transition = ease; sr.style.transition = ease;
          var setOut = function (v) {
            if (v === out) return; out = v;
            var q = v ? Math.ceil(window.innerWidth * 1.05) : 0;
            sl.style.transform = 'translate3d(' + (-q) + 'px,0,0)'; sr.style.transform = 'translate3d(' + q + 'px,0,0)';
          };
          var check = function () { var t = root.getBoundingClientRect().top; if (t < -40) setOut(true); else if (t > -8) setOut(false); };
          check(); window.addEventListener('scroll', check, { passive: true });
        }
      }
    })(roots[i]);
  }

  /* ---- discovery: CTA fades in under the last pinned card (Framer shows it only at the end) ---- */
  function initDiscovery() {
    var roots = document.querySelectorAll('[data-abh-disc]');
    for (var i = 0; i < roots.length; i++) (function (root) {
      var cards = root.querySelectorAll('.abh-disc__card');
      var layers = root.querySelectorAll('.abh-disc__layer');
      var wide = window.matchMedia && window.matchMedia('(min-width: 900px)').matches;
      var stack = root.querySelector('.abh-disc__stack');
      /* af127b (Greg 10/3 iPhone): the landed phone card must clear the pinned heading's sub line ("How we question, study, build,
         and prove") by 16px; the Framer's vh formula lets the card cover its second line at Safari heights. CSS takes
         max(vh formula, this). Deliberate deviation, phones only. */
      if (!wide) {
        var dHead = root.querySelector('.abh-disc__head'), dSub = root.querySelector('.abh-disc__sub') || (dHead && dHead.querySelector('h2'));
        var setMin = function () {
          if (!dHead || !dSub || !dSub.offsetHeight) return;
          var m = Math.ceil(dSub.getBoundingClientRect().bottom - dHead.getBoundingClientRect().top) + 16;
          root.style.setProperty('--abh-disc-min', m + 'px');
        };
        setMin(); window.addEventListener('load', setMin); window.addEventListener('resize', setMin);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(setMin);
      }
      /* af69: Framer natural doc tops (relative to the section) for the five layers, and the pile's release point.
         Measured every 200px at 1440 (2026-09-26): 318 / 1318 / 2118 / 3020 / 3918, release at 4000 with the
         CTA (102 under card 5) in card 5's layer -> every layer is padded to card 5's height so they release together. */
      /* af73b: the Framer pin is VIEWPORT-RELATIVE, not 318px. Measured live at 1440x900 and 1678x1000 (2026-09-26):
         landed card top = (vh - 264) / 2 (318 @900, 368 @1000), card N natural top = pin + (N-1)*vh (pitch = vh),
         pile releases at 4.5vh (4050 @900), section = 5.5vh (4950 / 5500). A fixed 318 landed the cards high under
         the heading on Greg's taller viewport and left the pile short of the section end. */
      var PIN = 318, RELEASE = 4050;
      function layout() {
        if (!wide || !stack || layers.length < 2) return;
        var i, h, VH = window.innerHeight;
        for (i = 0; i < layers.length; i++) { layers[i].style.paddingBottom = ''; layers[i].style.top = ''; }
        var lastH = layers[layers.length - 1].getBoundingClientRect().height;
        /* Framer landed card = 264 tall (glass box); centre THAT, not the layer (layer 5 also holds the CTA) and not the
           +-2deg bbox (316) or the padded box (288). 318 @900 / 368 @1000, verified on the harness. */
        PIN = Math.round((VH - 264) / 2);
        RELEASE = Math.round(VH * 4.5);
        root.style.setProperty('--abh-disc-pin', PIN + 'px');
        var prevBottom = null;
        for (i = 0; i < layers.length; i++) {
          h = layers[i].getBoundingClientRect().height;
          layers[i].style.top = PIN + 'px';
          if (i < layers.length - 1) layers[i].style.paddingBottom = Math.max(0, lastH - h) + 'px';
          var t = PIN + i * VH;
          layers[i].style.marginTop = prevBottom === null ? '' : Math.max(0, t - prevBottom) + 'px';
          prevBottom = t + lastH;
        }
        /* sticky bounds are the CONTENT box, so the release point is set with min-height, not padding */
        stack.style.paddingBottom = '0px';
        stack.style.minHeight = (RELEASE + lastH) + 'px';
      }
      /* af73b: Framer device entry, scroll-linked to the section top, sampled live every 100px at 900 and 1000 tall
         (scale vs section-top offset): -720 .40, -600 .47, -500 .55, -400 .77, -300 .86, -200 .94, -100 .995, 0 1. */
      var ENTRY = [[-720, .4], [-700, .405], [-600, .47], [-500, .55], [-400, .77], [-300, .86], [-200, .94], [-100, .995], [0, 1]];
      function entryScale(dt) {
        if (dt <= ENTRY[0][0]) return ENTRY[0][1];
        for (var k = 1; k < ENTRY.length; k++) if (dt <= ENTRY[k][0]) {
          var a = ENTRY[k - 1], b = ENTRY[k];
          return a[1] + (b[1] - a[1]) * (dt - a[0]) / (b[0] - a[0]);
        }
        return 1;
      }
      /* af106: phone pile tilt, scroll-linked like the Framer (390x844 + 428x629, 2026-09-30): card N's outer box carries
         translate(12t px, 12|t| px) rotate(t deg) with t = +-2 -> 0 (card 2 negative). Three phases in layer-height (vh) units
         from the section top: card 1 from -0.56vh, card 2 from 0.54vh, cards 3-5 together from 1.49vh; each lasts 1.19vh,
         mild ease-out. Cards 4 and 5 are therefore already flat when they rise (as on the Framer). */
      var TILT_START = [-0.56, 0.54, 1.49], TILT_SIGN = [1, -1, 1, 1, 1], LH = 0;
      function tiltPhone(dy) {
        if (!LH) LH = layers.length ? layers[0].offsetHeight : 0;
        if (!LH) return;
        for (var c = 0; c < cards.length; c++) {
          var t = 0;
          if (!reduced) {
            var p = clamp((dy - TILT_START[Math.min(c, 2)] * LH) / (1.19 * LH), 0, 1);
            t = 2 * (TILT_SIGN[c] || 1) * Math.pow(1 - p, 1.2);
          }
          var a = Math.abs(t) < 0.005 ? 0 : t;
          cards[c].style.transform = a ? 'translate(' + (12 * a).toFixed(2) + 'px,' + (12 * Math.abs(a)).toFixed(2) + 'px) rotate(' + a.toFixed(3) + 'deg)' : '';
        }
      }
      /* af111: Firefox without GPU compositing (BrowserStack Win VMs; users with hardware acceleration off) parses
         backdrop-filter but paints NOTHING for it (CSS.supports stays true), so a covered card's text read crisply through
         the rising card's 60% glass (Firefox 1280/1440/1920 device pass, 2026-10-01). Where the 70px blur does paint, that
         text is an invisible haze, so on Gecko only the covered card's text column is clipped at the top edge of the card
         above it. Chrome/Safari untouched. */
      var GECKO = !!(window.CSS && CSS.supports && CSS.supports('-moz-appearance', 'none'));
      var texts = [], glasses = [], clipLast = [];
      if (GECKO) for (var q = 0; q < cards.length; q++) {
        texts.push(cards[q].querySelector('.abh-disc__c')); glasses.push(cards[q].querySelector('.abh-disc__glass') || cards[q]); clipLast.push('');
      }
      function clipUnder() {
        if (!GECKO) return;
        for (var c = 0; c < texts.length - 1; c++) {
          var tx = texts[c]; if (!tx) continue;
          var tr = tx.getBoundingClientRect(), nr = glasses[c + 1].getBoundingClientRect();
          var cut = Math.ceil(tr.bottom - nr.top) + 2; /* +2: the next card's +-2deg corner */
          var v = (nr.top < tr.bottom && nr.bottom > tr.top) ? 'inset(0 0 ' + Math.min(Math.max(cut, 0), Math.ceil(tr.height) + 4) + 'px 0)' : '';
          if (v !== clipLast[c]) { tx.style.clipPath = v; clipLast[c] = v; }
        }
      }
      layout();
      window.addEventListener('resize', function () { LH = 0; });
      window.addEventListener('resize', layout);
      window.addEventListener('load', layout);
      jobs.push(function () {
        var r = root.getBoundingClientRect();
        var total = r.height - vh;
        if (total <= 0) return;
        root.classList.toggle('is-end', -r.top / total > 0.82);
        if (r.top < vh * 0.6) root.classList.add('is-in');
        /* af71: Framer entry scale is scroll-linked to the section's top (see CSS --abh-dt ranges) */
        root.style.setProperty('--abh-dt', String(Math.round(clamp(-r.top, -1000, 0)))); /* section top 700px below the viewport top -> -700 */
        root.style.setProperty('--abh-ds', entryScale(-r.top).toFixed(3));
        if (!wide) { tiltPhone(-r.top); clipUnder(); return; }
        /* af69: Framer pile — each rising card travels 600px from the viewport bottom (918 at 900 tall) to its pinned
           top (318); --abh-p 0->1 drives scale 1.1->1 and rotation rot0->0 in CSS. Landed cards keep p=1. */
        /* af72b: Framer harvest (temp/disc/fr.json) — the card keeps its +-2deg while rising and the rotation springs to 0
           only after it lands at 318 (time-based, ~1.4s); width stays 776 (no scale). */
        for (var c = 0; c < layers.length; c++) {
          var t = layers[c].getBoundingClientRect().top;
          layers[c].classList.toggle('is-landed', t <= PIN + 0.5);
        }
        clipUnder();
      });
    })(roots[i]);
  }

  /* ---- technology cards: exactly one open ---- */
  function initTech() {
    var rows = document.querySelectorAll('[data-abh-tech]');
    for (var i = 0; i < rows.length; i++) (function (row) {
      var cards = row.querySelectorAll('.abh-card');
      if (!cards.length) return;
      function open(card) {
        for (var c = 0; c < cards.length; c++) cards[c].classList.toggle('is-open', cards[c] === card);
      }
      var fine = window.matchMedia && window.matchMedia('(hover:hover) and (pointer:fine)').matches;
      for (var c = 0; c < cards.length; c++) (function (card) {
        if (fine) card.addEventListener('mouseenter', function () { open(card); });
        card.addEventListener('focus', function () { open(card); });
        card.addEventListener('click', function (e) {
          if (card.classList.contains('is-open')) return; /* open card: let the link navigate */
          e.preventDefault();
          open(card);
        });
      })(cards[c]);
    })(rows[i]);
  }

  /* ---- one system: blur-in when the section is mostly on screen ---- */
  /* ---- One System headline: Framer TextAnimation preset "focus" (see section comment). Characters are
     split at runtime (spaces stay plain), indexed in reading order across all lines; --i drives the .02s
     stagger in CSS. Replays on each entry at 40% visibility (component: replay true, threshold .4). ---- */
  function initOne() {
    var els = document.querySelectorAll('[data-abh-one]');
    if (!els.length) return;
    for (var i = 0; i < els.length; i++) (function (root) {
      var lines = root.querySelectorAll('[data-abh-one-line]'), idx = 0;
      for (var l = 0; l < lines.length; l++) {
        var text = lines[l].textContent, frag = document.createDocumentFragment(), words = text.split(/(\s+)/);
        for (var w = 0; w < words.length; w++) {
          if (!words[w]) continue;
          if (/^\s+$/.test(words[w])) { frag.appendChild(document.createTextNode(' ')); continue; }
          var word = document.createElement('span'); word.className = 'abh-one__w';
          var chars = Array.from ? Array.from(words[w]) : words[w].split('');
          for (var c = 0; c < chars.length; c++) { var ch = document.createElement('span'); ch.className = 'abh-one__c'; ch.style.setProperty('--i', idx++); ch.textContent = chars[c]; word.appendChild(ch); }
          frag.appendChild(word);
        }
        lines[l].textContent = ''; lines[l].appendChild(frag);
      }
      root.classList.add('is-split');
    })(els[i]);
    if (!('IntersectionObserver' in window) || reduced) {
      for (var k = 0; k < els.length; k++) els[k].classList.add('is-in');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.target.classList.toggle('is-in', e.isIntersecting); });
    }, { threshold: 0.4 });
    for (var j = 0; j < els.length; j++) io.observe(els[j]);
  }

  /* ---- technology row: cards rise in at ~2x the page as the row enters (Framer y=7271-7329) ---- */
  function initRise() {
    var rows = document.querySelectorAll('[data-abh-tech]');
    if (!rows.length || reduced) return;
    jobs.push(function () {
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i].getBoundingClientRect();
        if (r.top > vh * 1.2 || r.bottom < 0) continue;
        var p = clamp((vh - r.top) / (vh * 0.55), 0, 1);
        rows[i].style.setProperty('--abh-rise', ((1 - p) * 160).toFixed(1) + 'px');
      }
    });
  }

  /* ---- lifeforce: copy reveals word by word as each word crosses ~70% of the viewport (Framer spans) ---- */
  function initWords() {
    /* af69: the live Framer shows Lifeforce copy at opacity 1 from the moment it enters (curve sampled every 100px,
       2026-09-26); the earlier word-by-word dimming was ours, not the Framer's. Selector emptied, machinery kept. */
    var blocks = document.querySelectorAll('.abh-life__words-never');
    if (!blocks.length || reduced) return;
    var words = [];
    function wrap(node) {
      var kids = [];
      for (var i = 0; i < node.childNodes.length; i++) kids.push(node.childNodes[i]);
      for (var k = 0; k < kids.length; k++) {
        var n = kids[k];
        if (n.nodeType === 3) {
          var parts = n.nodeValue.split(/(\s+)/), frag = document.createDocumentFragment();
          for (var j = 0; j < parts.length; j++) {
            if (!parts[j]) continue;
            if (/^\s+$/.test(parts[j])) { frag.appendChild(document.createTextNode(parts[j])); continue; }
            var sp = document.createElement('span');
            sp.className = 'abh-w';
            sp.textContent = parts[j];
            frag.appendChild(sp);
            words.push(sp);
          }
          node.replaceChild(frag, n);
        } else if (n.nodeType === 1 && n.tagName !== 'BR' && n.tagName !== 'IMG') wrap(n);
      }
    }
    for (var b = 0; b < blocks.length; b++) wrap(blocks[b]);
    if (!words.length) return;
    jobs.push(function () {
      for (var w = 0; w < words.length; w++) {
        var r = words[w].getBoundingClientRect();
        if (r.bottom < -vh || r.top > vh * 2) continue;
        var o = clamp((vh * 0.78 - r.top) / (vh * 0.22), 0, 1);
        words[w].style.opacity = (0.14 + 0.86 * o).toFixed(3);
      }
    });
  }


  /* ---- af69: Framer text-reveal effects, harvested frame by frame from the live site (1440, 2026-09-26).
     Three units, each declared on the element:
       data-abh-split="char"  per character: opacity 0->1 + blur(10px)->0, no movement, ~240ms each, ~45ms stagger
                               (hero H1 on load after ~220ms; "The Primordial System of Fascia" on enter)
       data-abh-split="word"  per word: opacity 0->1 + translateY(10px)->0, ~240ms each, ~70ms stagger
                               (Two Languages, Truth Becomes Technology, Matrix heading, Body Never Forgot)
       data-abh-block="<ms>"  whole element fades 0->1 over ~400ms starting <ms> after it enters (copy that
                               follows a staggered heading: Two Languages 610, Technology intro 550, ...)
     One-shot: plays once when the element enters the viewport (Framer's appear effect), never on scroll-out.
     Text nodes only are wrapped; <br>, <strong>, <em> keep their structure. Reduced motion: everything visible. ---- */
  function initSplit() {
    /* Technology cards: Framer container appear = opacity 0->1 + translateY(150px)->0, all four start ~325ms after
       the row enters, transform durations 1.0 / 1.4 / 1.8 / 2.2s (measured ends 1322 / 1731 / ~2130 / 2533ms). */
    var rows = document.querySelectorAll('[data-abh-tech]');
    for (var r = 0; r < rows.length; r++) (function (row) {
      var cards = row.querySelectorAll('.abh-card');
      for (var c = 0; c < cards.length; c++) cards[c].style.setProperty('--abh-cd', (1 + .4 * c) + 's');
      row.classList.add('abh-cards');
      function go() { row.classList.add('is-in'); setTimeout(function () { row.classList.add('is-settled'); }, 2700); }
      if (reduced || !('IntersectionObserver' in window)) { go(); return; }
      var io = new IntersectionObserver(function (es) { if (es[0].isIntersecting) { go(); io.disconnect(); } }, { rootMargin: '0px 0px -10% 0px' });
      io.observe(row);
    })(rows[r]);
    var els = document.querySelectorAll('[data-abh-split],[data-abh-block]');
    if (!els.length) return;
    function wrapUnits(node, mode, list) {
      var kids = [];
      for (var i = 0; i < node.childNodes.length; i++) kids.push(node.childNodes[i]);
      for (var k = 0; k < kids.length; k++) {
        var n = kids[k];
        if (n.nodeType === 3) {
          var text = n.nodeValue, frag = document.createDocumentFragment();
          var parts = mode === 'char' ? text.split('') : text.split(/(\s+)/);
          for (var j = 0; j < parts.length; j++) {
            var p = parts[j];
            if (!p) continue;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(mode === 'char' ? '\u00a0' : p)); continue; }
            var sp = document.createElement('span');
            sp.className = 'abh-u';
            sp.textContent = p;
            frag.appendChild(sp);
            list.push(sp);
          }
          node.replaceChild(frag, n);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') wrapUnits(n, mode, list);
      }
    }
    for (var i = 0; i < els.length; i++) (function (el) {
      var mode = el.getAttribute('data-abh-split');
      var units = [];
      if (mode) {
        wrapUnits(el, mode, units);
        var stag = parseFloat(el.getAttribute('data-abh-stagger') || (mode === 'char' ? '45' : '70'));
        var base = parseFloat(el.getAttribute('data-abh-delay') || '0');
        for (var u = 0; u < units.length; u++) units[u].style.transitionDelay = (base + u * stag) + 'ms';
        el.classList.add('abh-split', 'abh-split--' + mode);
      } else el.classList.add('abh-block');
      var delay = parseFloat(el.getAttribute('data-abh-block') || '0');
      el.style.setProperty('--abh-rd', delay + 'ms');
      if (reduced || !('IntersectionObserver' in window)) { el.classList.add('is-in'); return; }
      var io = new IntersectionObserver(function (es) {
        if (es[0].isIntersecting) { el.classList.add('is-in'); io.disconnect(); }
      }, { rootMargin: '0px 0px -10% 0px' });
      io.observe(el);
    })(els[i]);
  }

  /* ---- one scroll loop for every scroll-linked job ---- */
  /* ---- ecosystem / Welcome Home (1:1 port of the Framer "WelcomeHome" code component, instance
     Dui9anhGI): 8 cubic-bezier cords from the link ends to the centre circle's tangent, glow filters,
     a node travelling each cord (4.5s, 1.5s when hovered) plus a second bright traveller (1.35s) on the
     hovered cord; hovering a link swaps the centre image to that item's image (swap keyframe .45s).
     Constants from the source: grid 1320x500, sidePad 48, linkSpread 110, connectorGap 24, ringPad 40,
     circleCy 250, cx 660, link rows 178/226/274/322, cord end rows 88/195/305/412. Cords render only
     when the section is >= 901px wide (component's own rule). ---- */
  function initEco() {
    var roots = document.querySelectorAll('[data-abh-eco]');
    var NS = 'http://www.w3.org/2000/svg';
    for (var i = 0; i < roots.length; i++) (function (root) {
      var svg = root.querySelector('[data-abh-eco-cords]');
      var links = root.querySelectorAll('[data-abh-eco-item]');
      var center = root.querySelector('[data-abh-eco-center]');
      if (!svg || !links.length) return;
      var uid = root.getAttribute('data-uid') || '';
      var soft = 'ab-eco-soft-' + uid, strong = 'ab-eco-strong-' + uid;
      var W = 1320, circle = 450, pad = 48, spread = 110, gap = 24, ringPad = 40, cy = 250, cx = 660;
      var rowsY = [178, 226, 274, 322], endY = [88, 195, 305, 412];
      var sideW = (W - circle - pad * 2) / 2;
      var leftEdge = sideW - spread, rightEdge = W - sideW + spread;
      var leftEnd = leftEdge + gap, rightEnd = rightEdge - gap;
      var R = circle / 2 + ringPad;
      function onCircle(y, side) { var dy = y - cy, dx = Math.sqrt(Math.max(0, R * R - dy * dy)); return side === 'left' ? cx - dx : cx + dx; }
      var paths = [];
      rowsY.forEach(function (y, k) { var x = onCircle(y, 'left'), ey = endY[k], d = Math.abs(x - leftEnd), c1 = Math.max(35, Math.min(d * .34, 95)), c2 = Math.max(45, Math.min(d * .46, 120)); paths.push('M ' + x + ' ' + y + ' C ' + (x - c1) + ' ' + y + ', ' + (leftEnd + c2) + ' ' + ey + ', ' + leftEnd + ' ' + ey); });
      rowsY.forEach(function (y, k) { var x = onCircle(y, 'right'), ey = endY[k], d = Math.abs(rightEnd - x), c1 = Math.max(35, Math.min(d * .34, 95)), c2 = Math.max(45, Math.min(d * .46, 120)); paths.push('M ' + x + ' ' + y + ' C ' + (x + c1) + ' ' + y + ', ' + (rightEnd - c2) + ' ' + ey + ', ' + rightEnd + ' ' + ey); });
      var groups = [];
      function el(tag, attrs) { var e = document.createElementNS(NS, tag); for (var a in attrs) e.setAttribute(a, attrs[a]); return e; }
      function motion(parent, d, dur) { var m = el('animateMotion', { dur: dur, repeatCount: 'indefinite', path: d }); parent.appendChild(m); return m; }
      paths.forEach(function (d) {
        var g = el('g', {});
        var cord = el('path', { d: d, 'class': 'ab-eco__cord', style: 'filter:url(#' + soft + ')' });
        var node = el('circle', { r: '3.5', 'class': 'ab-eco__node', style: 'filter:url(#' + soft + ')' });
        var nm = motion(node, d, '4.5s');
        g.appendChild(cord); g.appendChild(node); svg.appendChild(g);
        groups.push({ g: g, cord: cord, node: node, nm: nm, d: d, travel: null });
      });
      var hovered = -1;
      function setActive(idx) {
        if (idx === hovered) return;
        hovered = idx;
        groups.forEach(function (o, k) {
          var on = k === idx;
          o.cord.classList.toggle('is-active', on); o.node.classList.toggle('is-active', on);
          o.cord.style.filter = 'url(#' + (on ? strong : soft) + ')'; o.node.style.filter = o.cord.style.filter;
          o.nm.setAttribute('dur', on ? '1.5s' : '4.5s');
          if (on && !o.travel) { o.travel = el('circle', { r: '5', 'class': 'ab-eco__travel', style: 'filter:url(#' + strong + ')' }); motion(o.travel, o.d, '1.35s'); o.g.appendChild(o.travel); }
          else if (!on && o.travel) { o.g.removeChild(o.travel); o.travel = null; }
        });
        for (var k = 0; k < links.length; k++) links[k].classList.toggle('is-active', +links[k].getAttribute('data-abh-eco-item') === idx);
        if (center) {
          var src = idx >= 0 ? (links[idx] && links[idx].getAttribute('data-hover-img')) : null;
          var next = src || center.getAttribute('data-default-src');
          if (next && center.getAttribute('src') !== next) {
            center.setAttribute('src', next);
            center.classList.remove('is-swap'); void center.offsetWidth; center.classList.add('is-swap');
          }
        }
      }
      for (var k = 0; k < links.length; k++) (function (a) {
        var idx = +a.getAttribute('data-abh-eco-item');
        a.addEventListener('mouseenter', function () { setActive(idx); });
        a.addEventListener('focus', function () { setActive(idx); });
        a.addEventListener('mouseleave', function () { setActive(-1); });
        a.addEventListener('blur', function () { setActive(-1); });
        /* af80: on phones a tap has no hover, so the first tap only swaps the centre image (Framer's hover state) and a
           second tap on the same item follows the link. Placeholder targets ('#', the client hasn't sent the ecosystem
           link targets yet) never navigate on any device: a bare '#' scrolls the page to the top. */
        a.addEventListener('click', function (e) {
          var href = (a.getAttribute('href') || '').trim();
          var placeholder = href === '' || href === '#';
          var coarse = window.matchMedia && window.matchMedia('(hover:none)').matches;
          if (placeholder || (coarse && root.__abhTapped !== idx)) { e.preventDefault(); setActive(idx); root.__abhTapped = idx; }
        });
        a.addEventListener('blur', function () { if (root.__abhTapped === idx) root.__abhTapped = -1; });
      })(links[k]);
      /* component rule: cords only at >= 901px wide and while the section is near the viewport (120px margin) */
      var sys = root.querySelector('.ab-eco__system') || root;
      function size() { svg.style.display = sys.getBoundingClientRect().width >= 901 ? '' : 'none'; }
      size(); window.addEventListener('resize', size);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (es) { root.classList.toggle('is-near', es[0].isIntersecting); }, { rootMargin: '120px 0px' }).observe(root);
      } else root.classList.add('is-near');
    })(roots[i]);
  }

  /* ---- Scroll Reveal Image (Framer code component, auto/rows): N curtain strips slide out left->right
     one after another; overlay fades and the photo settles from 1.15 scale. Fires once when the box's
     top crosses the viewport middle (component rootMargin '0 0 -50vh 0'). CSS carries the values. ---- */
  function initReveal() {
    var boxes = document.querySelectorAll('[data-abh-sri]');
    for (var i = 0; i < boxes.length; i++) (function (box) {
      var rows = +box.getAttribute('data-rows') || 5, holder = box.querySelector('.abh-sri__rows');
      var delay = parseFloat(box.getAttribute('data-delay') || '.1'), stagger = parseFloat(box.getAttribute('data-stagger') || '.15'), dur = parseFloat(box.getAttribute('data-duration') || '1');
      if (holder) for (var r = 0; r < rows; r++) {
        var d = document.createElement('div'); d.className = 'abh-sri__row';
        d.style.top = 'calc(' + (r * 100 / rows) + '% - ' + (r === 0 ? 1 : 0) + 'px)';
        d.style.height = 'calc(' + (100 / rows) + '% + ' + ((r === 0 ? 1 : 0) + (r === rows - 1 ? 1 : 0)) + 'px)';
        d.style.transitionDelay = (delay + r * stagger) + 's'; d.style.transitionDuration = dur + 's';
        holder.appendChild(d);
      }
      box.classList.add('is-armed');
      if (reduced || !('IntersectionObserver' in window)) { box.classList.add('is-in'); return; }
      var io = new IntersectionObserver(function (es) { if (es[0].isIntersecting) { box.classList.add('is-in'); io.disconnect(); } }, { rootMargin: '0px 0px -' + Math.round(window.innerHeight * .5) + 'px 0px' });
      io.observe(box);
    })(boxes[i]);
  }

  function initLoop() {
    if (!jobs.length) return;
    var pending = false;
    function run() {
      pending = false;
      for (var i = 0; i < jobs.length; i++) jobs[i]();
    }
    function onScroll() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(run);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    /* af72: iOS Safari fires resize as the URL bar collapses mid-scroll; re-reading innerHeight there re-maps every pinned
       progress curve on the fly (visible as jitter). Only take a new vh when the width changes (rotation / real resize). */
    var vw0 = window.innerWidth;
    window.addEventListener('resize', function () { if (window.innerWidth !== vw0) { vw0 = window.innerWidth; vh = window.innerHeight; } onScroll(); });
    run();
  }

  function init() {
    initHeroScrub();
    initStacks();
    initMatrix();
    initClosing();
    initDiscovery();
    initTech();
    initOne();
    initRise();
    initWords();
    initEco();
    initReveal();
    initSplit();
    initLoop();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
